/* eslint-disable prefer-promise-reject-errors */
import { CognitoUser, CognitoUserAttribute, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { createContext, ReactNode, useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
// utils
import axios from 'src/utils/axios';
// routes
import { PATH_AUTH } from 'src/routes/paths';
// @types
import {
  ActionMap,
  AuthState,
  AuthUser,
  AVAILABLE_MFA_METHODS,
  AWSCognitoContextType,
  CUSTOM_ATTR_MFA_METHOD,
  MFA_METHOD_EMAIL,
  MFA_METHOD_TOTP,
  TOTP_AUTH_ISSUER,
} from 'src/@types/authentication';
// amplify
import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  setUpTOTP,
  signIn,
  SignInInput,
  signOut,
  updateMFAPreference,
  updateUserAttributes,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import * as config from 'src/config';
import { amplifyConfig, portalApi } from 'src/config';
// others
import { PortalUserEntity } from 'src/api/useUserApi';
import { authAPI } from 'src/features/mfa/api/authAPI';
import { UserMfaStatus } from 'src/features/mfa/api/userMfaStatus';
import { getBearerToken } from 'src/utils/getBearerToken';

export const UserPool = new CognitoUserPool({
  UserPoolId: amplifyConfig.aws_user_pools_id || '',
  ClientId: amplifyConfig.aws_user_pools_web_client_id || '',
});

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: amplifyConfig.aws_user_pools_id,
      userPoolClientId: amplifyConfig.aws_user_pools_web_client_id,
      loginWith: {
        oauth: {
          domain: amplifyConfig.cognitoDomain,
          scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [amplifyConfig.redirect_sign_in],
          redirectSignOut: [amplifyConfig.redirect_sign_out],
          responseType: 'code',
        },
      },
    },
  },
});

// MFA対応 start --------------
const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  method: 'cognito',
  isRequiredMfa: false, // 対象ユーザーのMFA認証要否
  isMfaAuthenticated: false, // MFA有効・無効
};

enum Types {
  auth = 'AUTHENTICATE',
  logout = 'LOGOUT',
  mfaAuth = 'MFA_AUTHENTICATE',
}

type AwsAuthPayload = {
  [Types.auth]: {
    isAuthenticated: boolean;
    user: AuthUser;
    method: string;
  };
  [Types.logout]: undefined;
  [Types.mfaAuth]: {
    isRequiredMfa: boolean;
    isMfaAuthenticated?: boolean;
  };
};
// MFA対応 end --------------

type AwsActions = ActionMap<AwsAuthPayload>[keyof ActionMap<AwsAuthPayload>];

// MFA対応 start --------------
export type SignInResult = {
  isRequiredMfa?: boolean;
  isRequiredMfaMethod?: boolean;
  isRequiredNewPassword?: boolean;
  mfaMethod?: AVAILABLE_MFA_METHODS;
};

export type UpdatePasswordResult = {
  data?: any;
  isUpdate?: boolean;
  requireMfa?: boolean;
  secretCode?: string;
};
// MFA対応 end --------------

const reducer = (state: AuthState, action: AwsActions) => {
  if (action.type === 'AUTHENTICATE') {
    const { isAuthenticated, user, method } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      method,
    };
  }

  // MFA対応 start --------------
  // MFA設定
  if (action.type === Types.mfaAuth) {
    const { isRequiredMfa, isMfaAuthenticated } = action.payload;
    return {
      ...state,
      isRequiredMfa,
      isMfaAuthenticated: isMfaAuthenticated === undefined ? state.isMfaAuthenticated : isMfaAuthenticated,
    };
  }
  // MFA対応 end --------------

  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

const AuthContext = createContext<AWSCognitoContextType | null>(null);
/**
 *
 * @param  param0 パラメータ
 * @return コンポーネント
 */
const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();
  const loginPathNameLocalStrageKey: string = 'callbackLoginPathName';

  const getUserAttributes = useCallback(
    (currentUser: CognitoUser): Record<string, any> =>
      new Promise((resolve, reject) => {
        currentUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
          } else {
            const results: Record<string, any> = {};

            attributes?.forEach((attribute) => {
              results[attribute.Name] = attribute.Value;
            });
            resolve(results);
          }
        });
      }),
    [],
  );

  const getMethod = useCallback(async (email: string) => {
    // AuthProviderが呼び出されるタイミングではenqueueSnackbarが使えないため axios.getで取得を行う
    try {
      // リクエスト
      const token = await getBearerToken();
      const result = await axios.get<PortalUserEntity[]>(portalApi + '/user/account', {
        headers: { Authorization: token },
        params: { filter: { where: { emailAddress: email }, take: 1 } },
      });

      // Resourceを取得 ACCOUNT, COGNITO, PLUTO
      if (result && result.data.length > 0) {
        return result.data[0].resource;
      }
    } catch (error) {
      // RootErrorBoundaryが反応するのはRouter入ってからなのでここでErrorをthrowしても反応しない
      // この後COGNITOが返ることでuseAuthで403となる
    }
    return 'COGNITO';
  }, []);

  const getSession = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const user = UserPool.getCurrentUser();

        if (user) {
          user.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
            if (err) {
              reject(err);
            } else {
              const attributes = await getUserAttributes(user);
              const token = session?.getIdToken().getJwtToken();

              const payload = session?.getIdToken().payload || {};
              Object.keys(payload).forEach((key) => {
                attributes[key] = payload[key];
              });

              // use the token or Bearer depend on the wait BE handle, by default amplify API only need to token.
              // axios.defaults.headers.common.Authorization = 'Bearer ' + token;
              axios.interceptors.request.use(async (request) => {
                const token = await new Promise((resolve2) => {
                  user.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
                    if (err) {
                      alert('セッションの有効時間が切れましたので再ログインしてください。(The session has expired, so please log in again.)'); // 遭遇頻度が低くsnackbarが使用できないためalertにて表示
                    } else {
                      const token = session?.getIdToken().getJwtToken();
                      resolve2(token);
                    }
                  });
                });
                request.headers.Authorization = 'Bearer ' + token;
                return request;
              });

              // MFA対応 start --------------
              // DataInsightIDによるログイン時
              if (!attributes?.identities) {
                // UserのMFA要否・設定状況を 再取得・設定
                const mfaStatus = await setUserMfaStatus(user.getUsername()); // MFA要否確認
                const { enabled } = await fetchMFAPreference(); // MFA状況
                dispatch({
                  type: Types.mfaAuth,
                  payload: {
                    isRequiredMfa: !mfaStatus?.isNoNeedMfa, // MFA要否
                    isMfaAuthenticated: !!enabled, // MFA必須ユーザーのMFA設定状況(enabledに有効なMFAタイプの設定あり=MFA認証済みと判断)
                  },
                });
              }
              // MFA対応 end --------------

              // Userの認証状況を再設定
              dispatch({
                type: Types.auth,
                payload: { isAuthenticated: true, user: attributes, method: await getMethod(attributes.email) },
              });
              resolve({
                user,
                session,
                headers: { Authorization: token },
              });
            }
          });
        } else {
          dispatch({
            type: Types.auth,
            payload: {
              isAuthenticated: false,
              user: null,
              method: 'COGNITO',
            },
          });
        }
      }),
    [getMethod, getUserAttributes],
  );

  const initial = useCallback(async () => {
    try {
      await getSession();
    } catch {
      dispatch({
        type: Types.auth,
        payload: {
          isAuthenticated: false,
          user: null,
          method: 'COGNITO',
        },
      });
    }
  }, [getSession]);

  const initialForAd = () => {
    const hubListenerCancel = Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signedIn':
          break;
        case 'signInWithRedirect':
          (async () => {
            await fetchAuthSession();
          })();
          break;
        case 'signedOut':
          break;
        case 'signInWithRedirect_failure':
          break;
        default:
          break;
      }
    });

    // 毎回実行される
    getUser().then(async (userData) => {
      await loginFromAzure(userData!);
    });

    return () => {
      hubListenerCancel();
    };
  };

  useEffect(() => {
    initialForAd();
    initial();
  }, [initial]);

  const getUser = async () => {
    return await getCurrentUser()
      .then((userData) => userData)
      .catch((err) => console.log('Not signed in err : ' + JSON.stringify(err)));
  };

  // MFA対応 start --------------
  // ログイン
  const login = useCallback(
    async (email, password): Promise<SignInResult> => {
      return await new Promise(async (resolve, reject) => {
        try {
          // ユーザーのMFA要否判定・MFA設定
          const mfaStatus = await setUserMfaStatus(email);
          // サインイン
          const signInInput: SignInInput = { username: email, password };
          // サインイン実行
          const { nextStep } = await signIn(signInInput);
          // 次のステップ確認
          switch (nextStep.signInStep) {
            // MFA認証が必要な場合
            case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
              // TOTP認証 - MFAコード入力画面へ遷移
              return resolve({ isRequiredMfa: true, mfaMethod: MFA_METHOD_TOTP } as SignInResult);

            case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
              // Eメール認証 - MFAコード入力画面へ遷移
              return resolve({ isRequiredMfa: true, mfaMethod: MFA_METHOD_EMAIL } as SignInResult);

            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
              // パスワード変更画面へ
              return resolve({ isRequiredNewPassword: true, isRequiredMfa: !mfaStatus?.isNoNeedMfa });

            // MFA認証不要 または、MFA方法が未設定の場合
            case 'DONE':
              // ログイン完了
              getSession();
              // MFA認証不要ユーザーの場合は、HOME画面へ
              if (mfaStatus?.isNoNeedMfa) return resolve({} as SignInResult);
              // 上記以外のユーザー(=MFA認証必須かつ、MFA方法選択必須)の場合は、ログイン完了後にMFA選択画面へ
              return resolve({ isRequiredMfaMethod: true } as SignInResult);
            default:
              throw new Error(`returned irregular response. nextStep: ${nextStep.signInStep} `);
          }
        } catch (error) {
          console.log(error);
          return reject(error);
        }
      });
    },
    [getSession],
  );

  /**
   * MFA認証要否チェック・MFA設定
   * @param email メールアドレス
   * @param password パスワード
   */
  const setUserMfaStatus = async (email: string): Promise<UserMfaStatus> => {
    // MFA要否チェック・MFA設定
    return await authAPI
      .setUserMfaStatus(email)
      .then(async (response) => {
        return response;
      })
      .catch((error) => {
        console.error('authAPI setUserMfaStatus failed');
        console.error(error);
        throw new Error(t('mfa.error.setMfaInternalError'));
      });
  };

  /**
   * MFA方法更新
   * @param method 設定するMFA方法
   */
  const updateMfaMethod = async (method: AVAILABLE_MFA_METHODS): Promise<void> => {
    try {
      // MFA認証方法を設定
      const totpStatus = method === MFA_METHOD_TOTP ? 'ENABLED' : 'DISABLED';
      const emailStatus = method === MFA_METHOD_EMAIL ? 'ENABLED' : 'DISABLED';
      await updateMFAPreference({ totp: totpStatus, email: emailStatus });

      // ユーザー選択MFA方法をユーザー属性に登録
      const output = await updateUserAttributes({
        userAttributes: {
          'custom:userSelectMfaMethod': method,
        },
      });
      console.log(output);
    } catch (err) {
      throw err;
    }
  };

  /**
   * MFA TOTPデバイス登録用URL取得
   */
  const setUpSoftwareToken = async (): Promise<URL | null> => {
    // TOTPデバイス登録用のURL取得
    const result = await setUpTOTP();
    // 利用アプリ名を設定してデバイス登録用URL発行
    const setupUri = result.getSetupUri(TOTP_AUTH_ISSUER);
    return setupUri;
  };

  /**
   * TOTPデバイスの登録
   * @param authCode 認証コード
   */
  const verifySoftwareToken = async (authCode: string): Promise<void> => {
    const authUser = await getCurrentUser();
    if (!authUser) throw new Error('cannot find login user.');
    try {
      // SOFTWARE TOKENの紐付け
      await verifyTOTPSetup({ code: authCode });
      // TOTPをMFA方法として選択
      await updateMfaMethod(MFA_METHOD_TOTP);
      // セッション更新
      dispatch({
        type: Types.mfaAuth,
        payload: {
          isRequiredMfa: state.isRequiredMfa, // MFA要否
          isMfaAuthenticated: true, // MFA必須ユーザーのMFA状況(=MFA認証済み)
        },
      });
    } catch (err) {
      throw err;
    }
  };

  /**
   * MFA認証チェック
   * @param authCode 認証コード
   */
  const verifyMfaCode = async (authCode: string) => {
    try {
      await confirmSignIn({ challengeResponse: authCode });
      // セッション更新
      getSession();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  /**
   * ログイン中ユーザーの登録済みのMFA方法取得
   */
  const getRegisteredMfaMethod = async (): Promise<AVAILABLE_MFA_METHODS | null> => {
    try {
      const result = await fetchUserAttributes();
      return (result && result[CUSTOM_ATTR_MFA_METHOD]) ?? null;
    } catch (err) {
      throw err;
    }
  };

  /**
   * ログアウト
   */
  const logout = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) return;
    } catch (e) {
      console.log(e);
      return;
    }

    // サインアウト処理実行
    await signOut();
    // ローカルStorageデータ削除
    removeLocalStorage();
    // Login画面の再読み込み
    window.location.replace('/');
  };
  // MFA対応 end --------------

  /**
   * ローカルストレージの不要データを念のため削除
   */
  const removeLocalStorage = () => {
    const keys = Object.keys(localStorage);
    const cognitoClientId = config.cognitoConfig.clientId;
    // Access Tokenの取得
    let i = keys.length;
    while (i--) {
      if (keys[i].includes(`${cognitoClientId}`) && localStorage.getItem(keys[i]) !== null) {
        localStorage.removeItem(keys[i]);
      }
    }
  };

  // MFA対応 start --------------
  /**
   * Sessionストレージに残るMFA関連データを削除
   */
  const removeMfaStatusFromSession = () => {
    let i = sessionStorage.length;
    while (i--) {
      const itemKey = sessionStorage.key(i);
      if (!!itemKey && itemKey.includes('CognitoSignInState')) {
        sessionStorage.removeItem(itemKey);
      }
    }
  };

  /**
   * ストレージに残る認証関連データをクリアして、ログイン画面へ移動
   */
  const moveToLoginWithClearStorage = () => {
    // Local・Session Storageからcognito関連情報を削除
    removeLocalStorage();
    removeMfaStatusFromSession();
    // Login画面の再読み込み
    window.location.replace('/');
  };

  /* ユーザーの初回ログイン時・管理者によるパスワードリセット後の、パスワード変更
   * @param newPassword 変更後のパスワード
   */
  const updatePassword = async (newPassword: string): Promise<SignInResult> => {
    try {
      // パスワード変更
      const { nextStep } = await confirmSignIn({ challengeResponse: newPassword });

      // 次のステップ確認
      switch (nextStep.signInStep) {
        // MFA方法設定済みの場合 - MFAコード入力画面へ遷移
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          return { isRequiredMfa: true, mfaMethod: MFA_METHOD_TOTP } as SignInResult;
        case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
          return { isRequiredMfa: true, mfaMethod: MFA_METHOD_EMAIL } as SignInResult;
        // MFA方法未設定の場合 - MFA選択画面へ
        case 'DONE':
          // ログイン完了
          getSession();
          return {} as SignInResult;
        default:
          throw new Error(`returned irregular response. nextStep: ${nextStep.signInStep} `);
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };
  // MFA対応 end --------------

  const register = (email: string, password: string, firstName: string, lastName: string) =>
    new Promise((resolve, reject) => {
      UserPool.signUp(
        email,
        password,
        [new CognitoUserAttribute({ Name: 'email', Value: email }), new CognitoUserAttribute({ Name: 'name', Value: `${firstName} ${lastName}` })],
        [],
        async (err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(undefined);
          window.location.href = PATH_AUTH.login;
        },
      );
    });

  const loginFromAzure = (currentUser: AuthUser) =>
    new Promise(async (resolve, reject) => {
      if (!currentUser) {
        dispatch({ type: Types.logout });
      } else {
        // const token = session?.getIdToken().getJwtToken();
        // use the token or Bearer depend on the wait BE handle, by default amplify API only need to token.
        // axios.defaults.headers.common.Authorization = token;
        // axios.defaults.headers.common.Authorization = 'Bearer ' + token;
        const user = UserPool.getCurrentUser()!;
        axios.interceptors.request.use(async (request) => {
          const token = await new Promise((resolve2) => {
            user.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
              if (err) {
                alert('セッションの有効時間が切れましたので再ログインしてください。(The session has expired, so please log in again.)'); // 遭遇頻度が低くsnackbarが使用できないためalertにて表示
              } else {
                const token = session?.getIdToken().getJwtToken();
                resolve2(token);
              }
            });
          });
          request.headers.Authorization = 'Bearer ' + token;
          return request;
        });

        // Userのsession情報取得、payloadから属性値取得
        const { tokens } = await fetchAuthSession();
        const payload = tokens?.idToken?.payload || {};

        const attributes: any = {};

        Object.keys(payload).forEach((key) => {
          attributes[key] = payload[key];
        });

        dispatch({
          type: Types.auth,
          payload: {
            isAuthenticated: true,
            user: attributes,
            method: await getMethod(attributes.email),
          },
        });
        const loginPath: string | null = localStorage.getItem(loginPathNameLocalStrageKey);
        if (loginPath) {
          localStorage.removeItem(loginPathNameLocalStrageKey);
          window.location.href = loginPath!;
        }
      }
    });

  const resetPassword = async (email: string) => {
    const user = new CognitoUser({
      Username: email,
      Pool: UserPool,
    });

    await new Promise((resolve, reject) => {
      user.forgotPassword({
        onSuccess: (data) => {
          // ここには入らない想定
          resolve(data);
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: (data) => {
          resolve(data);
        },
      });
    });
  };

  const newPassword = async (email: string, code: string, password: string) => {
    const user = new CognitoUser({
      Username: email,
      Pool: UserPool,
    });
    await new Promise((resolve, reject) => {
      user.confirmPassword(code, password, {
        onSuccess() {
          resolve(() => {});
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  };

  const updateProfile = () => {};
  return (
    <AuthContext.Provider
      value={{
        ...state,
        user: {
          displayName: state?.user?.['cognito:username'] || 'NO NAME',
          role: JSON.parse(state?.user?.['roles'] || '[]'),
          subCompaniesArray: JSON.parse(state?.user?.['subCompanies'] || '[]'),
          subDepartmentsArray: JSON.parse(state?.user?.['subDepartments'] || '[]'),
          subDivisionsArray: JSON.parse(state?.user?.['subDivisions'] || '[]'),
          ...state.user,
        },
        login,
        register,
        logout,
        updateProfile,
        resetPassword,
        loginFromAzure,
        newPassword,
        updatePassword,
        // MFA対応 start --------------
        setUpSoftwareToken,
        verifySoftwareToken,
        verifyMfaCode,
        updateMfaMethod,
        getRegisteredMfaMethod,
        moveToLoginWithClearStorage,
        // MFA対応 end --------------
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
